import { z } from 'zod'

import { EnumBuilder, type ZodInfer } from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import { type PackageFormat, PackageFormatSchema } from './package.models.ts'
import {
  NotificationPreferenceIdSchema,
  UserIdSchema,
} from './unique-id.models.ts'

/**
 * NotificationActivityType
 */
export const [
  NotificationActivityTypes,
  NotificationActivityTypeSchema,
  NotificationActivityTypeEnum,
] = EnumBuilder('new_project')
export type NotificationActivityType = ZodInfer<
  typeof NotificationActivityTypeSchema
>

const NotificationPreferenceBaseSchema = z.object({
  notificationPreferenceId: NotificationPreferenceIdSchema,
  userId: UserIdSchema,
  enabled: z.boolean(),
})

/**
 * new_project variant: optOuts are PackageFormat values
 */
export const NewProjectNotificationPreferenceSchema =
  NotificationPreferenceBaseSchema.extend({
    activityType: z.literal(NotificationActivityTypeEnum.new_project),
    optOuts: z.array(PackageFormatSchema),
  })

export type NewProjectNotificationPreference = ZodInfer<
  typeof NewProjectNotificationPreferenceSchema
>

/**
 * NotificationPreferenceDomainSelectModel — discriminated union by activityType
 */
export const NotificationPreferenceDomainSelectModelSchema =
  z.discriminatedUnion('activityType', [NewProjectNotificationPreferenceSchema])

export type NotificationPreferenceDomainSelectModel = ZodInfer<
  typeof NotificationPreferenceDomainSelectModelSchema
>

/**
 * Type guard for the new_project variant (optOuts are PackageFormat[]).
 */
export function isNewProjectNotificationPreference(
  preference: NotificationPreferenceDomainSelectModel
): preference is NewProjectNotificationPreference {
  return preference.activityType === NotificationActivityTypeEnum.new_project
}

/**
 * Type guard for a string that is a valid new_project opt-out key (PackageFormat).
 */
export function isNewProjectNotificationPreferenceKey(
  key: string
): key is PackageFormat {
  return PackageFormatSchema.safeParse(key).success
}

/**
 * Insert variant for new_project
 */
const NewProjectNotificationPreferenceInsertSchema =
  NewProjectNotificationPreferenceSchema.partial({
    notificationPreferenceId: true,
    enabled: true,
    optOuts: true,
  })

/**
 * NotificationPreferenceDomainInsertModel — discriminated union
 */
export const NotificationPreferenceDomainInsertModelSchema =
  z.discriminatedUnion('activityType', [
    NewProjectNotificationPreferenceInsertSchema,
  ])

export type NotificationPreferenceDomainInsertModel = ZodInfer<
  typeof NotificationPreferenceDomainInsertModelSchema
>

/**
 * Update variant for new_project: optOuts are PackageFormat values
 */
const NewProjectNotificationPreferenceUpdateSchema = z.object({
  activityType: z.literal(NotificationActivityTypeEnum.new_project),
  enabled: z.boolean().optional(),
  optOuts: z.array(PackageFormatSchema).optional(),
})

/**
 * NotificationPreferenceDomainUpdateModel — discriminated union by activityType.
 * activityType is required in the payload so optOuts are type-safe per variant.
 */
export const NotificationPreferenceDomainUpdateModelSchema =
  z.discriminatedUnion('activityType', [
    NewProjectNotificationPreferenceUpdateSchema,
  ])

export type NotificationPreferenceDomainUpdateModel = ZodInfer<
  typeof NotificationPreferenceDomainUpdateModelSchema
>

/**
 * NotificationPreferenceDomainSelectModelFilters
 */
export const NotificationPreferenceDomainSelectModelFiltersSchema = z
  .object({
    notificationPreferenceId: ModelFilterSchema(NotificationPreferenceIdSchema),
    userId: ModelFilterSchema(UserIdSchema),
    activityType: ModelFilterSchema(NotificationActivityTypeSchema),
    enabled: z.boolean(),
  })
  .partial()

export type NotificationPreferenceDomainSelectModelFilters = ZodInfer<
  typeof NotificationPreferenceDomainSelectModelFiltersSchema
>
